import { useEffect, useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Store } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, SectionTitle, Spinner } from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { useIdentities } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopShopSettings } from "./desktop/shop-settings.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  店铺设置
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这一页只管一件事：我的店对外长什么样、按什么价卖。**
 *    进货、订单、收益、团队各有各的页 —— 全堆在一页上，
 *    装修师傅在工地上单手划手机根本找不到东西。
 *
 * 三件事，都属于"对外呈现"：
 *    ① 客户看到的店名
 *    ② 收款联系方式（线下收款时买家要找的人）
 *    ③ 批量加价率（还没单独定价的商品按什么卖）
 */

export interface ShopSelf {
  id: number;
  name: string;
  displayName: string;
  contactName: string;
  contactPhone: string;
  status: string;
  markupRate: number | null;
}

function ShopSettings() {
  const search = useSearch({ from: "/shop-settings" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();

  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;

  const shop = useQuery({
    queryKey: ["merchant", "shop", orgId],
    queryFn: () => orgFetch<ShopSelf>("/shop", orgId!),
    enabled: Boolean(orgId),
  });

  const [displayName, setDisplayName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [rate, setRate] = useState("");

  /**
   * ⚠️ 表单初值要等数据回来再灌一次。
   *    只在 `useState` 里给初值的话，第一次渲染时数据还没到，
   *    输入框会永远是空的 —— 商家会以为自己什么都没设过。
   */
  useEffect(() => {
    if (!shop.data) return;
    setDisplayName(shop.data.displayName);
    setContactName(shop.data.contactName);
    setContactPhone(shop.data.contactPhone);
    setRate(shop.data.markupRate === null ? "" : String(+(shop.data.markupRate * 100).toFixed(2)));
  }, [shop.data]);

  const saveShop = useMutation({
    mutationFn: (body: Partial<ShopSelf>) =>
      orgFetch("/shop", orgId!, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", "shop", orgId] }),
  });

  const saveRate = useMutation({
    mutationFn: () =>
      orgFetch("/pricing/markup", orgId!, {
        method: "POST",
        body: JSON.stringify({ rate: Number(rate) / 100 }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", "shop", orgId] }),
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。**
   *    下面那个查询在 `orgId` 为空时是 disabled 的，而 disabled 的查询
   *    `isPending` 恒为 true —— 未登录、或登录了但名下没有店的人，
   *    看到的是一个**永远停不下来的圈**，页面上没有任何提示也没有出路。
   */
  if (!isPending && !orgId) {
    return (
      <SubPage title="店铺设置">
        <Empty title="你还不是商家" hint="店铺设置是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} />
      </SubPage>
    );
  }
  if (isPending || shop.isPending) return <SubPage title="店铺设置"><Spinner /></SubPage>;
  if (shop.error) return <SubPage title="店铺设置"><ErrorBlock error={shop.error} /></SubPage>;

  const s = shop.data!;
  const phoneMissing = !s.contactPhone?.trim();

  return (
    <SubPage title="店铺设置">
      <div className="space-y-3 px-3 py-3 pb-8">
        {/*
          🔴 **待审 / 停业时所有经营动作都会被拦**（改价、发券、上架、发货…）。
             不把状态摆到他眼前的话，他只会看到一连串莫名其妙的报错，
             不知道是被平台卡住了、更不知道该找谁。
        */}
        {s.status !== "active" && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800">
            {s.status === "pending"
              ? "资质还在审核中，通过之前改价、发券、上架、发货都会被拦下。请联系平台。"
              : s.status === "suspended"
                ? "店铺处于停业整顿状态，经营动作暂时都做不了。请联系平台。"
                : "店铺已清退。你账上的钱仍然可以提现，但不能再开展经营。"}
          </div>
        )}

        {/* ── ① 客户看到的店名 ── */}
        <SectionTitle>客户看到的名字</SectionTitle>
        <Card className="px-4 py-4">
          <Input
            label="店铺名称"
            value={displayName}
            placeholder={s.name}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          {/*
            ⚠️ 说清楚"留空会怎样" —— 不说的话商家会以为留空就是没有名字，
               于是被迫填一个跟工商名一模一样的东西。
          */}
          <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
            留空就用工商名「{s.name}」。可以起个客户好记的名字，
            比如「王师傅建材」而不是「深圳市××建材有限公司」。
          </p>
          <Button
            className="mt-3 w-full"
            loading={saveShop.isPending}
            onClick={() => saveShop.mutate({ displayName })}
          >
            保存名称
          </Button>
        </Card>

        {/* ── ② 收款联系方式 ── */}
        <SectionTitle>收款联系方式</SectionTitle>
        <Card className="px-4 py-4">
          {/*
            🔴 **线下收款模式下这不是可选项。**
               钱不经平台，买家下完单要直接联系你付款 ——
               没填的话他的付款页上只有一句"联系商家"却不知道联系谁，
               这单大概率就丢了。所以没填时用警示色主动提醒。
          */}
          {phoneMissing && (
            <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-[13px] leading-relaxed text-amber-800">
              还没填。客户下单后要联系你付款，没有电话他就只能干等 —— 这单大概率就丢了。
            </p>
          )}
          <div className="space-y-2">
            <Input
              label="称呼"
              value={contactName}
              placeholder="王师傅"
              onChange={(e) => setContactName(e.target.value)}
            />
            <Input
              label="电话"
              value={contactPhone}
              placeholder="13800138000"
              inputMode="tel"
              onChange={(e) => setContactPhone(e.target.value)}
            />
          </div>
          {/*
            ⚠️ **只填联系方式，不填收款账号。** 账号变更频繁（换卡、换收款码），
               存进系统里过期了就是把钱打给一个作废的账户；而且落库意味着
               任何能读商家资料的人都能拿到你的账户。让双方直接联系最稳。
          */}
          <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
            客户下单后会看到这个，联系你付款。
            <br />
            收款账号不用填在这儿 —— 电话里说更稳，也免得改了忘记同步。
          </p>
          <Button
            className="mt-3 w-full"
            loading={saveShop.isPending}
            onClick={() => saveShop.mutate({ contactName, contactPhone })}
          >
            保存联系方式
          </Button>
        </Card>

        {/* ── ③ 批量加价率 ── */}
        <SectionTitle>批量加价率</SectionTitle>
        <Card className="px-4 py-4">
          {/*
            🔴 **售价是你自己定的，不是系统按百分比自动算的。**
               加价率只是个批量工具：懒得一个个填的时候，
               先给所有商品一个默认售价。在「我的定价」里改过的那些，
               永远以你填的数为准，改这个比例也不会覆盖掉。

               不写清楚的话商家会以为价格是平台定的、自己只能调一个比例 ——
               而这个平台最核心的能力恰恰是"每个商家自己的一套价格"。
          */}
          <div className="flex gap-2">
            <Input
              inputMode="decimal"
              placeholder="15"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="flex-1"
            />
            <span className="self-center text-sm text-muted-foreground">%</span>
            <Button loading={saveRate.isPending} disabled={!rate} onClick={() => saveRate.mutate()}>
              保存
            </Button>
          </div>
          <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
            {s.markupRate === null
              ? "还没设过。设一个之后，你店里所有还没单独定价的商品都按这个比例卖。"
              : `当前 ${+(s.markupRate * 100).toFixed(2)}%。`}
            <br />
            这只是个省事的默认值 —— 在「我的定价」里单独改过的商品不受它影响。
          </p>
          {saveRate.error && <ErrorBlock error={saveRate.error} />}
          {saveRate.isSuccess && <p className="mt-2 text-xs text-emerald-600">已生效</p>}
        </Card>

        {saveShop.error && <ErrorBlock error={saveShop.error} />}

        {/* 客户视角预览 —— 改完名字最想做的下一件事就是"看看长什么样" */}
        <a
          href={`/shop/${s.id}`}
          className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-3.5 text-[14px] active:opacity-60"
        >
          <Store className="size-4 text-muted-foreground" />
          看看客户眼里的我的店
        </a>
      </div>
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function ShopSettingsRoute() {
  return useIsDesktop() ? <DesktopShopSettings /> : <ShopSettings />;
}

export const shopSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shop-settings",
  component: ShopSettingsRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
